import { useState } from 'react'
import { Link } from 'react-router-dom'
import { z } from 'zod'
import { apiClient, ApiClientError, CaptchaModal } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

const requestSchema = z.object({ email: z.string().email() })
const resetSchema = z.object({
  code: z.string().min(6).max(6),
  new_password: z.string().min(8),
})

/**
 * Single-screen 重置密码 — matches frame TexqP:
 *   邮箱 → [发送验证码] → 验证码输入 → 新密码 + 确认 → [重置密码]
 * Internally we still hit the two-step backend (forgot-password / reset-password).
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [newPwd2, setNewPwd2] = useState('')
  const [busy, setBusy] = useState(false)
  const [codeBusy, setCodeBusy] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [captchaOpen, setCaptchaOpen] = useState(false)
  // 2026-05-28 dg-fix-user2 §4 梯度 captcha
  const [requiredCount, setRequiredCount] = useState(1)

  const sendCode = async (captchaTokens?: string[]) => {
    setErrMsg(null)
    if (!email) {
      setErrMsg('请输入邮箱')
      return
    }
    setCodeBusy(true)
    try {
      const parsed = requestSchema.parse({ email })
      await apiClient.post('/gapi/public/auth/forgot-password', {
        ...parsed,
        captcha_tokens: captchaTokens ?? [],
      })
      setCodeSent(true)
    } catch (e) {
      if (e instanceof ApiClientError && e.code === 'CAPTCHA_REQUIRED') {
        const req = (e.details as { required_count?: number } | undefined)?.required_count ?? 1
        setRequiredCount(req)
        setCaptchaOpen(true)
        return
      }
      setErrMsg(e instanceof Error ? e.message : '发送失败')
    } finally {
      setCodeBusy(false)
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrMsg(null)
    if (newPwd !== newPwd2) {
      setErrMsg('两次密码不一致')
      return
    }
    setBusy(true)
    try {
      const parsed = resetSchema.parse({ code, new_password: newPwd })
      await apiClient.post('/gapi/public/auth/reset-password', { email, ...parsed })
      setSuccess(true)
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : '重置失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <MobileScreen>
      <TopBar />
      <main data-testid="TexqP" className="flex flex-col gap-6 px-6 pt-2">
        <div>
          <h1 className="text-[26px] font-bold text-[#1A1A1A]">重置密码</h1>
          <p className="mt-2 text-[13px] text-[#9CA3AF]">请输入您的注册邮箱，我们将发送验证码帮助您重置密码</p>
        </div>

        {success ? (
          <div className="rounded-lg bg-[#ebfaf6] p-4 text-[13px] text-[#0f7969]">
            密码已重置，请<Link to="/auth/login" className="font-semibold text-[#19caad]">重新登录</Link>。
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">邮箱地址</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入邮箱地址"
                autoComplete="email"
                className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">验证码</span>
              <div className="flex gap-2">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="请输入验证码"
                  maxLength={6}
                  inputMode="numeric"
                  className="h-12 flex-1 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
                />
                <button
                  type="button"
                  disabled={codeBusy || codeSent}
                  onClick={() => void sendCode()}
                  className="shrink-0 rounded-lg bg-[#19caad] px-4 text-[13px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
                >
                  {codeBusy ? '发送中…' : codeSent ? '已发送' : '发送验证码'}
                </button>
              </div>
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">新密码</span>
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="请输入新密码"
                autoComplete="new-password"
                className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-[14px] font-semibold text-[#1A1A1A]">确认密码</span>
              <input
                type="password"
                value={newPwd2}
                onChange={(e) => setNewPwd2(e.target.value)}
                placeholder="请再次输入新密码"
                autoComplete="new-password"
                className="h-12 rounded-lg bg-[#F1F2F4] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
              />
            </label>

            {errMsg && <p className="text-[13px] text-[#FF6B6B]">{errMsg}</p>}

            <button
              type="submit"
              disabled={busy}
              className="mt-2 h-12 rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
            >
              {busy ? '提交中…' : '重置密码'}
            </button>

            <Link to="/auth/login" className="self-center text-[13px] text-[#9CA3AF]">返回登录</Link>
          </form>
        )}
      </main>

      <CaptchaModal
        open={captchaOpen}
        onClose={() => setCaptchaOpen(false)}
        requiredCount={requiredCount}
        onVerified={(tokens) => {
          setCaptchaOpen(false)
          void sendCode(tokens)
        }}
      />
    </MobileScreen>
  )
}
